// Athlete / coach profile page (see `features/athlete-profile.md`), opened from
// an athlete row on the company profile page. It aggregates the athlete's
// photos, basic information, club & teams, sport profiles and training into one
// read view. Company access is enough to view; MANAGE_ATHLETES unlocks editing
// (the same AthleteDialog used in the roster) and the public-visibility toggle
// + share link. The server enforces the same rules on every request.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SportsIcon from '@mui/icons-material/Sports';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import GroupsIcon from '@mui/icons-material/Groups';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { Athlete, AthleteProfile } from '../domain';
import {
  fetchAthlete,
  fetchAthleteProfile,
  setAthleteProfileVisibility,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { AthleteDialog } from './AthleteDialog';
import { AthleteAccessSection } from './AthleteAccessSection';
import { PhotoGallery } from './PhotoGallery';
import { publicProfileUrl } from './public/profileSharePath';
import { groupSchedulePatterns, isMinor, schedulePatternLabel } from '../domain';

interface AthleteProfileViewProps {
  personalNumber: string;
  /** Return to the roster. */
  onBack: () => void;
}

export function AthleteProfileView({ personalNumber, onBack }: AthleteProfileViewProps) {
  const { hasPermission } = useAuth();
  const { t, localeCode } = useTranslation();
  const canManage = hasPermission('MANAGE_ATHLETES');

  const [athlete, setAthlete] = useState<Athlete | null>(null);
  const [profile, setProfile] = useState<AthleteProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextAthlete, nextProfile] = await Promise.all([
        fetchAthlete(personalNumber),
        fetchAthleteProfile(personalNumber),
      ]);
      setAthlete(nextAthlete);
      setProfile(nextProfile);
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.athleteProfile.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [personalNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(localeCode, { dateStyle: 'medium', timeZone: 'UTC' }),
    [localeCode],
  );

  const shareUrl = useMemo(
    () => (athlete ? publicProfileUrl(athlete.profileToken, window.location.origin) : ''),
    [athlete],
  );

  const toggleVisibility = async (isPublic: boolean) => {
    if (!athlete) return;
    setBusy(true);
    setError(null);
    try {
      setAthlete(await setAthleteProfileVisibility(athlete.personalNumber, isPublic));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.athleteProfile.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (e.g. insecure context); the link stays selectable.
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!athlete || !profile) {
    return (
      <Box sx={{ maxWidth: 880, mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          {t('admin.athleteProfile.back')}
        </Button>
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {error ?? t('admin.athleteProfile.notFound')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        {t('admin.athleteProfile.back')}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header. */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Avatar
          src={athlete.photoUrls[0] ?? undefined}
          alt={athlete.fullName}
          sx={{ width: 72, height: 72 }}
        >
          {athlete.isCoach ? <SportsIcon fontSize="large" /> : <DirectionsRunIcon fontSize="large" />}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h5" noWrap title={athlete.fullName}>
            {athlete.fullName}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              variant="outlined"
              label={athlete.personalNumber}
              sx={{ fontFamily: 'monospace' }}
            />
            {athlete.isCoach && (
              <Chip size="small" color="secondary" label={t('admin.athletes.coachChip')} />
            )}
            {profile.club && (
              <Chip size="small" variant="outlined" label={profile.club.name} />
            )}
          </Stack>
        </Box>
        {canManage && (
          <Tooltip title={t('admin.athleteProfile.editAria', { name: athlete.fullName })}>
            <span>
              <Button
                size="small"
                startIcon={<EditIcon />}
                disabled={busy}
                onClick={() => setEditOpen(true)}
              >
                {t('admin.athleteProfile.edit')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {/* Basic information. */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('admin.athleteProfile.basicInfo')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(`admin.athletes.gender.${athlete.gender}`)}
          {' · '}
          {t('admin.athletes.born', {
            date: dateFormat.format(new Date(`${athlete.dateOfBirth}T00:00:00Z`)),
          })}
          {profile.age !== null && ` · ${t('admin.athletes.age', { count: profile.age })}`}
        </Typography>
        {athlete.coachDescription && (
          <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
            {athlete.coachDescription}
          </Typography>
        )}
      </Paper>

      {/* Sharing & visibility (managers only). */}
      {canManage && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('admin.athleteProfile.visibility.title')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={athlete.profilePublic}
                disabled={busy}
                onChange={(e) => void toggleVisibility(e.target.checked)}
              />
            }
            label={t('admin.athleteProfile.visibility.public')}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              athlete.profilePublic
                ? 'admin.athleteProfile.visibility.publicHint'
                : 'admin.athleteProfile.visibility.privateHint',
            )}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              fullWidth
              value={shareUrl}
              label={t('admin.athleteProfile.shareLink')}
              slotProps={{ htmlInput: { readOnly: true } }}
              onFocus={(e) => e.target.select()}
            />
            <Tooltip title={copied ? t('admin.athleteProfile.copied') : t('admin.athleteProfile.copyLink')}>
              <IconButton aria-label={t('admin.athleteProfile.copyLink')} onClick={() => void copyLink()}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('admin.athleteProfile.openLink')}>
              <span>
                <IconButton
                  aria-label={t('admin.athleteProfile.openLink')}
                  component="a"
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={!athlete.profilePublic}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      {/* Who can sign in to this profile (see `features/athlete-access.md`). */}
      <AthleteAccessSection
        personalNumber={athlete.personalNumber}
        athleteName={athlete.fullName}
        isMinor={isMinor(athlete.dateOfBirth)}
      />

      {/* Photos. */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('admin.athleteProfile.photos')}
        </Typography>
        <PhotoGallery
          urls={athlete.photoUrls}
          alt={athlete.fullName}
          emptyLabel={t('admin.athleteProfile.noPhotos')}
          labels={{
            close: t('gallery.close'),
            download: t('gallery.download'),
            previous: t('gallery.previous'),
            next: t('gallery.next'),
          }}
        />
      </Paper>

      {/* Sport profiles. */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('admin.athleteProfile.sports')}
        </Typography>
        {profile.sports.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('admin.athleteProfile.noSports')}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {profile.sports.map((sport) => (
              <Stack key={sport.sportId} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip color="info" variant="outlined" label={sport.sportName} />
                {Object.entries(sport.attributes).map(([key, value]) => (
                  <Chip
                    key={key}
                    size="small"
                    variant="outlined"
                    label={`${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`}
                  />
                ))}
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Clubs & teams. */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('admin.athleteProfile.teams')}
        </Typography>
        {profile.teams.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('admin.athleteProfile.noTeams')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {profile.teams.map((team) => (
              <Stack key={team.teamId} direction="row" spacing={1.5} alignItems="center">
                <GroupsIcon color="disabled" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" noWrap>
                    {team.name}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined" label={team.sportName} />
                    {team.teamRole && <Chip size="small" variant="outlined" label={team.teamRole} />}
                    {team.tier && <Chip size="small" variant="outlined" label={team.tier} />}
                  </Stack>
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Training. */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('admin.athleteProfile.training')}
        </Typography>
        {profile.training.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('admin.athleteProfile.noTraining')}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {profile.training.map((entry) => (
              <Box key={`${entry.role}-${entry.trainingClassId}`}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <FitnessCenterIcon color="disabled" />
                  <Typography variant="subtitle1" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                    {entry.name}
                  </Typography>
                  <Chip
                    size="small"
                    color={entry.role === 'COACH' ? 'secondary' : 'default'}
                    variant="outlined"
                    label={t(`admin.athleteProfile.classRole.${entry.role}`)}
                  />
                </Stack>
                {entry.schedule.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, ml: 4.5 }}>
                    {groupSchedulePatterns(entry.schedule).map((pattern, i) => (
                      <Chip
                        key={i}
                        size="small"
                        variant="outlined"
                        label={schedulePatternLabel(pattern, (day) =>
                          t(`admin.trainingClasses.weekdays.${day}`),
                        )}
                      />
                    ))}
                  </Stack>
                )}
                {entry.role === 'ATHLETE' && (
                  <Box sx={{ ml: 4.5, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('admin.athleteProfile.attendance', {
                        present: entry.attendance.present,
                        marked: entry.attendance.marked,
                      })}
                      {entry.attendance.marked > 0 &&
                        ` · ${Math.round(entry.attendance.rate * 100)}%`}
                    </Typography>
                    {entry.attendance.marked > 0 && (
                      <LinearProgress
                        variant="determinate"
                        value={entry.attendance.rate * 100}
                        sx={{ mt: 0.5, maxWidth: 240, borderRadius: 1 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Edit dialog: reuse the roster's AthleteDialog. */}
      <AthleteDialog
        open={editOpen}
        companyId={athlete.companyId}
        athlete={athlete}
        isCoach={athlete.isCoach}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => {
          setAthlete(saved);
          setEditOpen(false);
          void load();
        }}
      />
    </Box>
  );
}
